import { env } from 'src/config/env';
import posthog, { type CaptureOptions, type PostHog, type PostHogInterface } from 'posthog-js';
import { toPosthogEvent } from './toPosthogEvent';
import { toSnakeCase } from './posthogTaxonomy';
import { getOrCreateInstallId } from '../logs/installId';

const USER_PROPERTY_KEY_MAP: Record<string, string> = {
  familyId: 'family_id',
  userType: 'user_type',
  yob: 'year_of_birth',
};

export const INTERNAL_FLAG_CLEARED_KEY = 'bao_internal_flag_cleared_v1';

export const clearInternalUserFlagOnce = (
  ph: Pick<typeof posthog, 'setPersonProperties'>,
): void => {
  try {
    if (localStorage.getItem(INTERNAL_FLAG_CLEARED_KEY)) return;
    ph.setPersonProperties({ $internal_or_test_user: false });
    localStorage.setItem(INTERNAL_FLAG_CLEARED_KEY, '1');
  } catch {
    // storage unavailable — retry next init
  }
};

export type PosthogConsent = 'granted' | 'denied' | 'pending';

let replaySampledIn = false;

const registerInstallId = () => {
  try {
    posthog.register({ install_id: getOrCreateInstallId() });
  } catch {
    // analytics identity must never block boot
  }
};

// Initialise PostHog.
export const initPosthog = (options?: { consent?: PosthogConsent }): void => {
  if (!env.posthogProjectToken) return;

  const consent = options?.consent;
  const gated = consent !== undefined;
  const granted = consent === 'granted';

  // Session replay: env-driven per-session sampling.
  const replayPercent = Number(
    env.posthogReplayPercent || 0,
  );
  replaySampledIn = Math.random() * 100 < replayPercent;

  posthog.init(env.posthogProjectToken, {
    api_host: env.posthogHost,
    defaults: '2026-01-30',
    // posthog-js flags localhost as internal and Capacitor serves from localhost, so every native user was flagged.
    internal_or_test_user_hostname: null,
    loaded: gated
      ? (ph: PostHogInterface) => {
          if (granted) {
            posthog.opt_in_capturing({ captureEventName: false });
            clearInternalUserFlagOnce(ph);
          } else {
            ph.clear_opt_in_out_capturing();
          }
        }
      : clearInternalUserFlagOnce,
    disable_session_recording: gated && !granted ? true : !replaySampledIn,
    capture_performance: { network_timing: true },
    session_recording: {
      recordHeaders: false,
      recordBody: false,
    },
    ...(gated
      ? {
          cookieless_mode: 'on_reject' as const,
          opt_out_capturing_by_default: true,
          opt_out_persistence_by_default: true,
        }
      : {}),
  });

  // Registered HERE, not in initAnalytics(): initAnalytics is called at src/index.tsx:453, AFTER
  // runBootSequence() at :334, and is fire-and-forget — so boot events emit before it has even been
  // called.
  // initPosthog runs at module scope, so only here do early-boot events get it; registerPosthogSuperProps
  // guards on __loaded, which may not hold yet.
  if (gated && !granted) return;
  registerInstallId();
};

export const grantPosthogConsent = (): void => {
  if (!posthog.__loaded) return;
  posthog.opt_in_capturing({ captureEventName: false });
  registerInstallId();
  clearInternalUserFlagOnce(posthog);
  if (replaySampledIn) posthog.startSessionRecording();
};

export const revokePosthogConsent = (): void => {
  if (!posthog.__loaded) return;
  posthog.opt_out_capturing();
};

// Per-call delivery overrides. Default capture goes into posthog-js's batch queue (3 s timer,
// flushed on `pagehide` via sendBeacon). Callers that cannot wait (the OTA success event before the
// WebView swap, the camera-open event) pass
// `{ send_instantly: true, transport: 'sendBeacon' }`. Deliberately a narrow pick: nothing else in
// `CaptureOptions` should be reachable from feature code.
export type AnalyticsCaptureOptions = Pick<CaptureOptions, 'send_instantly' | 'transport'>;

export const capturePosthogEvent = (
  legacyName: string,
  params?: Record<string, unknown>,
  options?: AnalyticsCaptureOptions,
): void => {
  if (!posthog.__loaded) return;
  try {
    const { event, properties } = toPosthogEvent(legacyName, params ?? {});
    // Two-arg call when no options are passed: keeps the wire and existing call-site assertions
    // unchanged.
    if (options) posthog.capture(event, properties, options);
    else posthog.capture(event, properties);
  } catch (e) {
    if (env.isDev) console.warn('[analytics] posthog capture failed', e);
  }
};

export const isPosthogAnonymousOnly = (): boolean =>
  !!posthog.__loaded &&
  posthog.config.cookieless_mode === 'on_reject' &&
  posthog.has_opted_out_capturing();

export const registerPosthogSuperProps = (props: Record<string, unknown>): void => {
  if (!posthog.__loaded) return;
  posthog.register(props);
};

export const unregisterPosthogSuperProp = (key: string): void => {
  if (!posthog.__loaded) return;
  posthog.unregister(key);
};

export const setPosthogFamilyGroup = (familyId: string): void => {
  if (!posthog.__loaded || isPosthogAnonymousOnly()) return;
  posthog.group('family', familyId);
  notifyFamilyGroupReadiness();
};

let familyGroupPropertiesSentForKey: string | null = null;

// Set only when a push (below) lands while posthog-js already has a `/flags` request in flight for
// this family: that request's own callback still resolves with the pre-push evaluation (posthog-js
// queues the reload behind `_additionalReloadRequested` instead of racing a second request — see
// `_callFlagsEndpoint` in posthog-featureflags.js, checked against posthog-js 1.393.6), so the very
// next `onFeatureFlags` firing for this key is stale, not the owed reload.
let familyGroupSkipNextFiringForKey: string | null = null;

const familyGroupReadinessListeners = new Set<() => void>();

const notifyFamilyGroupReadiness = (): void => {
  for (const listener of familyGroupReadinessListeners) listener();
};

export const subscribeFamilyGroupReadiness = (listener: () => void): (() => void) => {
  familyGroupReadinessListeners.add(listener);
  return () => {
    familyGroupReadinessListeners.delete(listener);
  };
};

export const isFamilyGroupReady = (): boolean => {
  if (!posthog.__loaded || familyGroupPropertiesSentForKey === null) return false;
  return posthog.getGroups()?.family === familyGroupPropertiesSentForKey;
};

export const clearFamilyGroupPropertiesPushed = (): void => {
  familyGroupPropertiesSentForKey = null;
  familyGroupSkipNextFiringForKey = null;
  notifyFamilyGroupReadiness();
};

export const __resetFamilyGroupPropertiesSentForTests = (): void => {
  clearFamilyGroupPropertiesPushed();
};

export const familyGroupPropertiesSentFor = (): string | null => familyGroupPropertiesSentForKey;

export const consumeFamilyGroupSkipNextFiring = (familyId: string): boolean => {
  if (familyGroupSkipNextFiringForKey !== familyId) return false;
  familyGroupSkipNextFiringForKey = null;
  return true;
};

export const updatePosthogFamilyGroup = (
  familyId: string,
  properties: Record<string, unknown>,
): boolean => {
  if (!posthog.__loaded || isPosthogAnonymousOnly()) return false;
  const requestInFlight = !!(posthog.featureFlags && Reflect.get(posthog.featureFlags, '_requestInFlight'));
  posthog.group('family', familyId, properties);
  familyGroupPropertiesSentForKey = familyId;
  familyGroupSkipNextFiringForKey = requestInFlight ? familyId : null;
  notifyFamilyGroupReadiness();
  return true;
};

export const toCleanUserPropertyKey = (key: string): string =>
  USER_PROPERTY_KEY_MAP[key] ?? toSnakeCase(key);

export const setPosthogPersonProps = (props: Record<string, unknown>): void => {
  if (!posthog.__loaded || isPosthogAnonymousOnly()) return;
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    clean[toCleanUserPropertyKey(key)] = value;
  }
  posthog.setPersonProperties(clean);
};

export const setPosthogPersonPropsForFlags = (props: Record<string, unknown>): void => {
  if (!posthog.__loaded) return;
  posthog.setPersonPropertiesForFlags(props);
};

// Kept in module state because forgetPreviousIdentityFlags() wipes every flag person property on
// an identity change; the device outlives the user, so it is re-applied after the reset.
export type DeviceFlagProps = {
  device_model: string;
  device_os_name: string;
  device_os_version: string;
  device_memory?: number;
};

let deviceFlagProps: DeviceFlagProps | null = null;

export const setPosthogDeviceFlagProps = (props: DeviceFlagProps): void => {
  deviceFlagProps = { ...props };
  if (!posthog.__loaded) return;
  // reload=false: they ride the next /flags call.
  posthog.setPersonPropertiesForFlags(deviceFlagProps, false);
};

export const __resetDeviceFlagPropsForTests = (): void => {
  deviceFlagProps = null;
};

export const PERSISTED_FLAG_ANSWER_KEYS = [
  '$enabled_feature_flags',
  '$active_feature_flags',
  '$feature_flag_payloads',
  '$feature_flag_details',
  '$feature_flag_request_id',
  '$feature_flag_evaluated_at',
] as const;

export const forgetPreviousIdentityFlags = (ph: PostHog = posthog): void => {
  if (!ph.__loaded) return;
  ph.resetPersonPropertiesForFlags(false);
  // reload=false: the identify() that follows reloads once.
  if (deviceFlagProps) ph.setPersonPropertiesForFlags(deviceFlagProps, false);
  ph.featureFlags.reset();
  for (const key of PERSISTED_FLAG_ANSWER_KEYS) ph.unregister(key);
};

let renderedProfileDistinctId: string | null = null;

export const setRenderedProfileDistinctId = (id: string | null): void => {
  renderedProfileDistinctId = id;
};

export const warnOnFlagReadUnderForeignIdentity = (flagKey: string): void => {
  if (!env.isDev) return;
  // Device-bound keys are read before identify by design.
  if (flagKey.startsWith('device_')) return;
  if (renderedProfileDistinctId === null) return;
  if (!posthog.__loaded || isPosthogAnonymousOnly()) return;
  const current = posthog.get_distinct_id();
  if (current === renderedProfileDistinctId) return;
  console.warn(
    `[feature flags] "${flagKey}" read while PostHog holds "${current}" and the app renders ` +
      `"${renderedProfileDistinctId}": identify the profile before any flag read ` +
      '(managing-feature-flags skill, identity first).',
  );
};
